/**
 * View component for /admin/authoring/studies/:studyId/study-users/:studyUserId
 *
 * Displays a single studyUser from the 'byId' map in the studyUser reducer
 * as defined by the 'selected' property
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// import actions
import * as courseActions from '../../course/courseActions';
import * as moduleCertificateActions from '../../moduleCertificate/moduleCertificateActions';
import * as siteActions from '../../site/siteActions';
import * as siteLocationActions from '../../siteLocation/siteLocationActions';
import * as sponsorActions from '../../sponsor/sponsorActions';
import * as studyActions from '../../study/studyActions';
import * as studyRoleActions from '../../studyRole/studyRoleActions';
import * as studyUserActions from '../studyUserActions';
import * as contentModuleActions from '../../contentModule/contentModuleActions';
import * as userActions from '../../user/userActions';
import * as userEventActions from '../../userEvent/userEventActions';

// import global components
import AlertModal from '../../../global/components/modals/AlertModal.js.jsx';
import AuthoringLayout from '../../admin/components/AuthoringLayout.js.jsx';
import Base from "../../../global/components/BaseComponent.js.jsx";
import Breadcrumbs from '../../../global/components/routing/Breadcrumbs.js.jsx';
import Modal from '../../../global/components/modals/Modal.js.jsx';
import { SearchInput } from '../../../global/components/forms';

// import module components
import EditCertModal from '../../moduleCertificate/components/EditCertModal.js.jsx';
import RequiredTrainingTable from '../../contentModule/components/RequiredTrainingTable.js.jsx';
import StudyUserInfo from '../components/StudyUserInfo.js.jsx';
import UserActivityCard from '../../user/components/UserActivityCard.js.jsx';

import _ from 'lodash';
import { DateTime } from 'luxon';
// import studyUser css modules
import studyUserStyles from '../studyUserModuleStyles.css';

import certUtils from '../../../global/utils/certUtils';
import filterUtils from '../../../global/utils/filterUtils';

class AdminSingleStudyUser extends Base {
  constructor(props) {
    super(props);
    this.state = {
      courseIds: []
      , isCreateCertModalOpen: false
      , isUpdateCertModalOpen: false
      , selectedCertificate: null
      , cmIds: []
    }
    this._bind(
      '_handleCloseEditModal'
      , '_handleQueryCerts'
      , '_handleSetPagination'
      , '_handleSortCerts'
      , '_resetPagination'
      , '_setPerPage'
    )
  }

  componentDidMount() {
    const { dispatch, match } = this.props;

    // 1.14 send user access tracking
    dispatch(userEventActions.sendCreateUserEvent({
      eventType: "user-access"
      , eventAction: "study-user-record"
      , _groupUser: match.params.studyUserId
    }));

    dispatch(studyActions.fetchSingleIfNeeded(match.params.studyId));
    dispatch(studyUserActions.fetchSingleIfNeeded(match.params.studyUserId)).then(suRes => {
      dispatch(studyRoleActions.fetchSingleIfNeeded(suRes.item._groupRole));
      dispatch(siteActions.fetchListIfNeeded('_group', match.params.studyId, 'number', suRes.item.siteNumber)).then(siteRes => {
        if(siteRes.success && siteRes.list && siteRes.list[0] && siteRes.list[0]._siteLocation) {
          dispatch(siteLocationActions.fetchSingleIfNeeded(siteRes.list[0]._siteLocation));
        }
      })
      dispatch(sponsorActions.fetchSingleIfNeeded(suRes.item._organization));
      dispatch(moduleCertificateActions.fetchListIfNeeded('_user', suRes.item._user));
      dispatch(moduleCertificateActions.setFilter({query: '', sortBy: 'name'}, '_user', suRes.item._user));
      dispatch(moduleCertificateActions.setPagination({page: 1, per: 50}, '_user', suRes.item._user));
      dispatch(userActions.fetchSingleIfNeeded(suRes.item._user));
      dispatch(userEventActions.fetchListIfNeeded('_user', suRes.item._user));
      // fetch user specific course. if not set, return most recent.
      if(suRes.item._course) {
        dispatch(courseActions.fetchListIfNeeded('_id', suRes.item._course)).then(courseRes => {
          // console.log("SPECIFIC COURSE tms")
          const courseIds = courseRes.list.map(c => c._id);
          const cmIds = courseRes.list.map(c => c._contentModules).reduce((a,b) => a.concat(b), []);
          // console.log(cmIds);
          const uniqueCMIds = Array.from(new Set(cmIds));
          // console.log(uniqueCMIds);
          this.setState({
            courseIds
            , cmIds: uniqueCMIds
          });
          dispatch(contentModuleActions.fetchListIfNeeded('_id', uniqueCMIds));
        })
      } else {
        dispatch(courseActions.fetchListIfNeeded('_group', match.params.studyId, 'status', 'published')).then(courseRes => {
          let orderedCourses = courseRes.list
          orderedCourses = orderedCourses.sort((a,b) => new Date(b.created) - new Date(a.created) )
          // console.log("ORDERED COURSES", orderedCourses)
          let courseIds = orderedCourses.map(c => c._id);
          const cmIds = orderedCourses[0] ? orderedCourses[0]._contentModules : [];
          const uniqueCMIds = Array.from(new Set(cmIds));
          this.setState({
            courseIds
            , cmIds: uniqueCMIds
          });
          dispatch(contentModuleActions.fetchListIfNeeded('_id', uniqueCMIds));
        })
      }
    });
  }

  _handleCloseEditModal() {
    // close it and then invalidate and refetch the cert list
    let { selectedCertificate } = this.state;
    this.props.dispatch(moduleCertificateActions.invalidateList('_user', selectedCertificate._user));
    this.props.dispatch(moduleCertificateActions.fetchListIfNeeded('_user', selectedCertificate._user));

    this.setState({isUpdateCertModalOpen: false, selectedCertificate: null })
  }

  _handleSortCerts(sortBy) {
    const { dispatch, match, moduleCertificateList } = this.props;
    let newFilter = moduleCertificateList.filter;
    if(moduleCertificateList.filter.sortBy && moduleCertificateList.filter.sortBy.indexOf("-") < 0) {
      sortBy = "-" + sortBy;
    } else {
      sortBy = sortBy.substring(0)
    }
    newFilter.sortBy = sortBy;
    dispatch(moduleCertificateActions.setFilter(newFilter));
  }

  _resetPagination() {
    const { dispatch, match } = this.props;
    dispatch(moduleCertificateActions.setPagination({
      page: 1,
      per: parseInt(this.state.perPage)
    }));
  }

  _setPerPage(per) {
    const { dispatch, match, moduleCertificateList } = this.props;
    let newPagination = moduleCertificateList.pagination;
    newPagination.per = parseInt(per);
    newPagination.page = 1;
    dispatch(moduleCertificateActions.setPagination(newPagination));
    this.setState({perPage: per});
  }

  _handleSetPagination(newPagination) {
    const { dispatch, match } = this.props;
    dispatch(moduleCertificateActions.setPagination(newPagination));
  }

  _handleQueryCerts(e) {
    // console.log("DEBUG 0")
    const { dispatch, match, moduleCertificateList, userStore } = this.props;

    const selectedUser = userStore.selected.getItem();

    // always defaulting the page to page 1 so we can see our results
    let pagination = {};
    pagination.page = 1;
    pagination.per = moduleCertificateList.pagination.per;
    this._handleSetPagination(pagination);
    // continue query logic
    dispatch(moduleCertificateActions.setFilter({
      query: e.target.value.toLowerCase()
      , sortBy: moduleCertificateList.filter.sortBy
    },'_user', selectedUser._id));
  }

  render() {
    const {
      courseStore
      , location
      , loggedInUser
      , match
      , moduleCertificateList
      , moduleCertificateStore
      , paginatedCertList
      , siteStore 
      , siteLocationStore 
      , sortedCertList
      , sponsorStore
      , studyStore
      , studyRoleStore 
      , studyUserStore
      , contentModuleStore
      , userStore
      , userEventStore
    } = this.props;

    const {
      courseIds
      , cmIds
    } = this.state;

    /**
     * use the selected.getItem() utility to pull the actual studyUser object from the map
     */
    const selectedStudyUser = studyUserStore.selected.getItem();
    const selectedStudy = studyStore.selected.getItem();
    const selectedSponsor = sponsorStore.selected.getItem();
    const selectedUser = userStore.selected.getItem();

    const selectedStudyRole = studyRoleStore.selected.getItem();

    // site things 
    const studySiteList = selectedStudyUser && siteStore.lists._group && siteStore.lists._group[match.params.studyId] && siteStore.lists._group[match.params.studyId].number ? siteStore.lists._group[match.params.studyId].number[selectedStudyUser.siteNumber] : {};
    const studySites = selectedStudyUser ? siteStore.util.getList('_group', match.params.studyId, 'number', selectedStudyUser.siteNumber) : null;
    
    const site = studySites && studySites[0];
    const siteLocation = siteLocationStore.selected.getItem(); 


    const tmList = contentModuleStore.lists._id ? contentModuleStore.lists._id[cmIds] : {};
    const contentModules = contentModuleStore.util.getList('_id', cmIds);

    const courseList = courseStore.lists._group ? courseStore.lists._group[match.params.studyId] : {};
    const courses = courseStore.util.getList('_group', match.params.studyId);

    const userEvents = selectedStudyUser ? userEventStore.util.getList('_user', selectedStudyUser._user) : null;

    const isEmpty = (
      !selectedStudyUser
      || !selectedStudyUser._id
      || studyUserStore.selected.didInvalidate
      || !selectedStudy
      || !selectedUser
      || !selectedUser._id
      || !contentModules
    );

    const isFetching = (
      !courseList
      || courseList.isFetching
      || studyUserStore.selected.isFetching
      || !tmList
      || tmList.isFetching
    )

    // console.log("TEST", paginatedCertList)
    // console.log(cmIds);
    // console.log(paginatedCertList.map(c => c._contentModule));
    // console.log(paginatedCertList.filter(c => cmIds.indexOf(c._contentModule) > -1).map((moduleCertificate, i) => moduleCertificate));

    return (
      <AuthoringLayout>
        <Helmet
          title={"View User | Pro-ficiency"}
        />
        <div className="flex">
        <Helmet
          title="View User | Pro-ficiency"
        />
          <section className="section">
            <div className="yt-container">
              { isEmpty ?
                (isFetching ? <h2>Loading...</h2> : <h2>Empty.</h2>)
                :
                <div style={{ opacity: isFetching ? 0.5 : 1 }}>
                  <Breadcrumbs links={location.state.breadcrumbs} />
                  <div className="yt-row with-gutters">
                    <div className="yt-col full s_80 m_60">
                      <StudyUserInfo
                        course={courseStore.byId[selectedStudyUser._course]}
                        loggedInUser={loggedInUser}
                        site={site}
                        siteLocation={siteLocation}
                        sponsor={selectedSponsor}
                        study={selectedStudy}
                        studyRole={selectedStudyRole}
                        studyUser={selectedStudyUser}
                        user={selectedUser}
                        viewingAs="admin"
                      />
                    </div>
                    <div className="yt-col full s_20 m_40">
                      <UserActivityCard
                        studyUser={selectedStudyUser}
                        user={selectedUser}
                        userEvents={userEvents}
                        viewingAs="admin"
                      />
                    </div>
                    <div className="yt-col full">
                      <RequiredTrainingTable
                        moduleCertificates={sortedCertList}
                        cmIds={cmIds}
                        studyUser={selectedStudyUser}
                      />
                    </div>
                    <div className="yt-col full">
                      <div className="content-container">
                        <div className="yt-row space-between">
                          <p><strong>Certificates of Completion</strong></p>
                          <div>
                            <button className="yt-btn x-small " onClick={() => this.setState({isCreateCertModalOpen: true})}> Create new certificate </button>
                          </div>
                        </div>
                        <hr/>
                        <div className="yt-toolbar">
                          <div className="yt-tools ">
                            <div className="search">
                              <SearchInput
                                name="query"
                                value={moduleCertificateList.filter.query || ''}
                                change={this._handleQueryCerts}
                                placeholder="Search..."
                                required={false}
                                />
                            </div>
                          </div>
                        </div>
                        <div className="table-wrapper">
                          <table className="yt-table striped">
                            <thead>
                              <tr>
                                <th className="sortable" onClick={()=> this._handleSortCerts('contentModuleTitle')}>
                                  Content module simulation
                                  {moduleCertificateList.filter.sortBy === 'contentModuleTitle' ?
                                    <i className="ion ion-ios-arrow-down" />
                                    : moduleCertificateList.filter.sortBy === '-contentModuleTitle' ?
                                    <i className="ion ion-ios-arrow-up" />
                                    : null
                                  }
                                </th>
                                <th className="_10 sortable" onClick={()=> this._handleSortCerts('status')}>
                                  Status
                                  {moduleCertificateList.filter.sortBy === 'status' ?
                                    <i className="ion ion-ios-arrow-down" />
                                    : moduleCertificateList.filter.sortBy === '-status' ?
                                    <i className="ion ion-ios-arrow-up" />
                                    : null
                                  }
                                </th>
                                <th className="_20 sortable" onClick={()=> this._handleSortCerts('issuedDate')}>
                                  Issued
                                  {moduleCertificateList.filter.sortBy === 'issuedDate' ?
                                    <i className="ion ion-ios-arrow-down" />
                                    : moduleCertificateList.filter.sortBy === '-issuedDate' ?
                                    <i className="ion ion-ios-arrow-up" />
                                    : null
                                  }
                                </th>
                                <th className="_20 sortable" onClick={()=> this._handleSortCerts('expires')}>
                                  Expiration Date
                                  {moduleCertificateList.filter.sortBy === 'expires' ?
                                    <i className="ion ion-ios-arrow-down" />
                                    : moduleCertificateList.filter.sortBy === '-expires' ?
                                    <i className="ion ion-ios-arrow-up" />
                                    : null
                                  }
                                </th>
                                <th colSpan="2"></th>
                              </tr>
                            </thead>
                            <tbody>
                              { sortedCertList.filter(c => cmIds.indexOf(c._contentModule) > -1).map((moduleCertificate, i) =>
                                <tr key={moduleCertificate._id + i}>
                                  <td>{contentModuleStore.byId[moduleCertificate._contentModule] ? contentModuleStore.byId[moduleCertificate._contentModule].title : ''}</td>
                                  <td>{moduleCertificate.status}</td>
                                  <td>{!moduleCertificate.issuedDate ? DateTime.fromISO(moduleCertificate.created).toLocaleString(DateTime.DATE_MED) : DateTime.fromISO(moduleCertificate.issuedDate).toLocaleString(DateTime.DATE_MED)}</td>
                                  <td>{moduleCertificate.expires ?  DateTime.fromISO(moduleCertificate.expires).toLocaleString(DateTime.DATE_MED) : <em>Never</em> }</td>
                                  <td><Link to={`${match.url}/certs/${moduleCertificate._id}`}>View</Link></td>
                                  <td><a onClick={() => this.setState({selectedCertificate: moduleCertificate, isUpdateCertModalOpen: true})}>Update</a></td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                  <EditCertModal
                    closeModal={() => this.setState({isCreateCertModalOpen: false})}
                    formType="Create"
                    isOpen={this.state.isCreateCertModalOpen}
                    moduleCertificate={moduleCertificateStore.defaultItem}
                    selectedUserId={selectedUser._id}
                    contentModules={contentModules}
                  />
                  { this.state.selectedCertificate ?
                    <EditCertModal
                      closeModal={this._handleCloseEditModal}
                      formType="Update"
                      isOpen={this.state.isUpdateCertModalOpen}
                      moduleCertificate={this.state.selectedCertificate}
                      selectedUserId={selectedUser._id}
                      contentModules={contentModules}
                    />
                    :
                    null
                  }
                </div>
              }
            </div>
          </section>
        </div>
      </AuthoringLayout>
    )
  }
}

AdminSingleStudyUser.propTypes = {
  dispatch: PropTypes.func.isRequired
}

const mapStoreToProps = (store) => {
  /**
  * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
  * differentiated from the React component's internal state
  */
  const selectedUser = store.user.selected.getItem();
  const moduleCertificateStore = store.moduleCertificate;
  const moduleCertificateList = selectedUser && selectedUser._id && moduleCertificateStore.lists._user ? moduleCertificateStore.lists._user[store.user.selected.id] : {};
  const tmMap = store.contentModule.byId;

  let sortedCertList = [];
  let paginatedCertList = [];
  let filteredCertsByQuery = [];

  if(selectedUser && selectedUser._id && moduleCertificateList) {
    const pagination = moduleCertificateList.pagination;
    const filter = moduleCertificateList.filter;
    const query = filter ? filter.query : '';
    const sortBy = filter ? filter.sortBy : 'status';

    // FILTER BY QUERY
    let queryTestString = ("" + query).toLowerCase().trim();
    // queryTestString = queryTestString.replace(/[^a-zA-Z0-9]/g,''); // replace all non-characters and numbers
    filteredCertsByQuery = moduleCertificateList && moduleCertificateList.items ? moduleCertificateList.items.filter((mcId) => {
      return filterUtils.filterModuleCert(queryTestString, moduleCertificateStore.byId[mcId], tmMap);
    }) : []

    // SORT THE LIST
    sortedCertList = filteredCertsByQuery.map((item) => {
      let newItem = moduleCertificateStore.byId[item];
      newItem.contentModuleTitle = tmMap[newItem._contentModule] ? tmMap[newItem._contentModule].title : '';
      return newItem;
    });
    switch(sortBy) {
      case 'status':
        sortedCertList = _.orderBy(sortedCertList, [item => item.status.toLowerCase()], ['asc']);
        break;
      case '-status':
        sortedCertList = _.orderBy(sortedCertList, [item => item.status.toLowerCase()], ['desc']);
        break;
      case 'contentModuleTitle':
        sortedCertList = _.orderBy(sortedCertList, [item => item.contentModuleTitle.toLowerCase()], ['asc']);
        break;
      case '-contentModuleTitle':
        sortedCertList = _.orderBy(sortedCertList, [item => item.contentModuleTitle.toLowerCase()], ['desc']);
        break;
      case 'expires':
        sortedCertList = _.orderBy(sortedCertList, [item => item.expires], ['asc']);
        break;
      case '-expires':
        sortedCertList = _.orderBy(sortedCertList, [item => item.expires], ['desc']);
        break;
      case 'issuedDate':
        sortedCertList = _.orderBy(sortedCertList, [item => item.issuedDate], ['asc']);
        break;
      case '-issuedDate':
        sortedCertList = _.orderBy(sortedCertList, [item => item.issuedDate], ['desc']);
        break;
      case 'updated':
        sortedCertList = _.orderBy(sortedCertList, [item => item.updated], ['asc']);
        break;
      case '-updated':
        sortedCertList = _.orderBy(sortedCertList, [item => item.updated], ['desc']);
        break;
      default:
        sortedCertList = _.orderBy(sortedCertList, [item => item.status.toLowerCase()], ['asc']);
    }
    // apply pagination
    let start = pagination ? (pagination.page - 1) * pagination.per : 1;
    let end = pagination ? start + pagination.per : 0;
    paginatedCertList = _.slice(sortedCertList, start, end);

  }

  return {
    courseStore: store.course
    , loggedInUser: store.user.loggedIn.user 
    , moduleCertificateList
    , moduleCertificateStore
    , paginatedCertList
    , siteStore: store.site 
    , siteLocationStore: store.siteLocation 
    , sortedCertList
    , sponsorStore: store.sponsor
    , studyStore: store.study
    , studyRoleStore: store.studyRole 
    , studyUserStore: store.studyUser
    , contentModuleStore: store.contentModule
    , userStore: store.user
    , userEventStore: store.userEvent
  }
}

export default withRouter(
  connect(
    mapStoreToProps
  )(AdminSingleStudyUser)
);



// WEBPACK FOOTER //
// modules/studyUser/views/AdminSingleStudyUser.js.jsx